import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonInput,
  IonItem,
  IonLabel,
  IonLoading,
  IonModal,
  IonSelect,
  IonSelectOption,
  IonTitle,
  IonToolbar,
  useIonAlert,
  useIonViewDidEnter,
  useIonViewWillLeave,
} from "@ionic/react";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { decodeParam } from "../util/ApiHelper";
import { useParams } from "react-router";
import {
  TranslationMessagesContext,
  TranslationsContext,
} from "../util/Translations";
import {
  ProcessWorkOrderActionListData,
  changeStatus,
} from "../api/MobileApproval";
import useGetWorkOrder from "../hooks/useGetWorkOrder";
import { useWorkOrderStore } from "../store/wo-order";
import { useShallow } from "zustand/react/shallow";
import useGetValidStatusValues from "../hooks/work-order/useGetValidStatusValues";
import { useWoOfflineUpdatesStore } from "../store/wo-offline-updates";
import { useOfflineStore } from "../store/offline-mode";

interface StatusModalProps {
  isOpen: boolean;
  cancel: () => void;
  confirm: () => void;
}

const Status: React.FC<StatusModalProps> = (props) => {
  const { isOpen, cancel, confirm } = props;
  const { translations } = useContext(TranslationsContext);
  const { workOrderId } = useParams<{ workOrderId: string }>();
  const displayWo = decodeParam(workOrderId);
  const [showLoading, setShowLoading] = useState(false);
  const [comments, setComments] = useState<string>();
  const { translatedMessages } = useContext(TranslationMessagesContext);

  const [presentAlert] = useIonAlert();
  const { trigger } = useGetWorkOrder();
  const { getValidStatusValues: triggerGetValidStatusValues } =
    useGetValidStatusValues();
  const workOrder = useWorkOrderStore((state) =>
    state.getWorkOrderById(displayWo)
  );

  const addStatusToUpdate = useWoOfflineUpdatesStore(
    (state) => state.addStatusToUpdate
  );

  const validStatusValues = useWorkOrderStore(
    useShallow((state) => state.getValidStatusValuesForWo(displayWo))
  );
  const isOffline = useOfflineStore((state) => state.isOffline);

  const [status, setStatus] = useState<string>(workOrder?.Status ?? "");

  const getWorkOrder = useCallback(async () => {
    if (workOrder) return; //got it from cache
    const response = await trigger(displayWo);
    setStatus(String(response.data.Status));
  }, [displayWo, trigger, workOrder]);

  const getValidStatusValues = useCallback(async () => {
    if (validStatusValues?.length) return; //got it from cache
    await triggerGetValidStatusValues(displayWo);
  }, [displayWo, triggerGetValidStatusValues, validStatusValues]);

  const setup = useCallback(async () => {
    setShowLoading(true);
    await getWorkOrder();
    await getValidStatusValues();
    setShowLoading(false);
  }, [getValidStatusValues, getWorkOrder]);

  useEffect(() => {
    if (!isOpen) return; //no need to make calls as status box is not opened
    setup();
  }, [isOpen, setup]);

  const updateStatus = async () => {
    if (!displayWo || !status) {
      return;
    }
    const data: ProcessWorkOrderActionListData = {
      RequestObject: {
        WorkOrderActionListData: [
          { WoBase: displayWo, Status: status, Comments: comments || "" },
        ],
      },
      Confirmation: {},
    };

    if (isOffline) {
      addStatusToUpdate({ ...data, woNumber: displayWo });
      setComments("");
      presentAlert({
        message:
          translatedMessages["MobOfflineStatusChanged"]?.MessageText ||
          "Status saved in offline store.",
        buttons: [
          {
            text: translations["lbl_btn_ok"] || "OK",
          },
        ],
      });
      cancel(); //just close the dialog
      return; //will sync it once user is back online
    }

    changeStatus(data).then((response) => {
      if (
        response.data.ResultMessage?.Success === true &&
        response.data.WoResultsList[0].WoResult.Success
      ) {
        presentAlert({
          header: response.data?.WoResultsList[0].WoResult?.MessageList[0].Text,
          backdropDismiss: false,
          buttons: [
            {
              text: translations["lbl_btn_ok"] || "OK",
              handler: () => {
                confirm();
                setComments("");
              },
            },
          ],
        });
      } else if (
        response.data?.WoResultsList[0].WoResult?.MessageList[0].MessageType ===
        1
      ) {
        presentAlert({
          header: response.data?.WoResultsList[0].WoResult?.MessageList[0].Text,
          buttons: [
            {
              text: response.data?.WoResultsList[0].WoResult?.MessageList[0]
                ?.ValidResponses[0].DisplayText,
              role: "cancel",
            },
            {
              text: response.data?.WoResultsList[0].WoResult?.MessageList[0]
                ?.ValidResponses[1].DisplayText,
              handler: () => {
                confirm();
                getWorkOrder();
                setComments("");
              },
            },
          ],
        });
      } else {
        presentAlert({
          header: response.data?.WoResultsList[0].WoResult?.MessageList[0].Text,
          backdropDismiss: false,
          buttons: [
            {
              text: translations["lbl_btn_ok"] || "OK",
            },
          ],
        });
      }
    });
  };

  return (
    <IonModal isOpen={isOpen} onDidDismiss={cancel}>
      <IonLoading isOpen={showLoading} />
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonButton
              onClick={() => {
                cancel();
                getWorkOrder();
              }}
            >
              {translations["lbl_btn_cancel"] || "Cancel"}
            </IonButton>
          </IonButtons>
          <IonTitle>
            {translations["lbl_mob_changestatus"] || "Change Status"}
          </IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={() => updateStatus()} disabled={!status}>
              {translations["lbl_btn_save"] || "Save"}
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <IonItem>
          <IonLabel position="stacked">
            {translations["lbl_woworkorder_status"] || "Status"}*
          </IonLabel>
          <IonSelect
            interface="popover"
            value={status}
            onIonChange={(ev) => {
              setStatus(ev.detail.value);
            }}
          >
            {(validStatusValues ?? []).map((value, index) => {
              return (
                <IonSelectOption key={index} value={value.Code}>
                  {value.Code}
                  {!!value.Description && ` - ${value.Description}`}
                </IonSelectOption>
              );
            })}
          </IonSelect>
        </IonItem>
        <IonItem>
          <IonLabel position="stacked">
            {translations["lbl_category_comments"] || "Comments"}
          </IonLabel>
          <IonInput
            placeholder={translations["lbl_enter comments"] || "Enter Comments"}
            onIonInput={(ev) => {
              setComments(String(ev.target.value));
            }}
          ></IonInput>
        </IonItem>
      </IonContent>
    </IonModal>
  );
};

export default Status;
